import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, Shield, Layout, AlertOctagon, Mail, Activity, RotateCcw, UserPlus } from 'lucide-react';
import ToolCard from '../shared/ToolCard';
import NotificationTester from '../tools/NotificationTester';
import MFATester from '../../Shared/MFATester';
import ToastTester from '../tools/ToastTester';
import EmailTester from '../tools/EmailTester';
import WindowActivationButtonDemo from '../../__tests__/WindowActivationButtonDemo';
import AllSurveyWindows from '../../__tests__/AllWindows';
import AdaptestTester from '../tools/AdaptestTester';
import ParticipantResetTool from '../tools/ParticipantResetTool';
import SeedTestParticipantTool from '../tools/SeedTestParticipantTool';

const TestingTools = ({ user }) => {
  const [selectedTool, setSelectedTool] = useState(null);

  const testingTools = [
    {
      id: 'notifications',
      icon: Bell,
      label: 'Notification System',
      description: 'Test in-app notifications, badges, and delivery',
      action: 'Test Notifications'
    },
    {
      id: 'mfa',
      icon: Shield,
      label: 'Multi-Factor Authentication',
      description: 'Test MFA setup, verification, and recovery',
      action: 'Test MFA'
    },
    {
      id: 'toasts',
      icon: AlertOctagon,
      label: 'Toast Messages',
      description: 'Test toast notification system and email alerts',
      action: 'Test Toasts'
    },
    {
      id: 'windows',
      icon: Layout,
      label: 'Survey Windows',
      description: 'Survey window activation and management testing',
      action: 'Manage Windows'
    },
    {
      id: 'irt-scoring',
      icon: Activity,
      label: 'IRT Scoring',
      description: 'Test Adaptest IRT scoring API with sample data',
      action: 'Test IRT Scoring'
    },
    {
      id: 'participant-reset',
      icon: RotateCcw,
      label: 'Reset Test Participant',
      description: 'Reopen windows and clear history so a test participant can retake assessments',
      action: 'Open Tool'
    },
    {
      id: 'seed-test-participant',
      icon: UserPlus,
      label: 'Seed Test Participant',
      description: 'One-shot: create a fresh participant + schedule for a given survey so you can log in and test immediately',
      action: 'Open Tool'
    }
  ];

  const handleToolClick = (tool) => {
    setSelectedTool(selectedTool === tool.id ? null : tool.id);
  };

  const renderToolContent = (toolId) => {
    switch (toolId) {
      case 'notifications':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <NotificationTester />
          </div>
        );
        
      case 'mfa':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6">Multi-Factor Authentication Testing</h3>
            <MFATester />
          </div>
        );
        
      case 'toasts':
        return (
          <div className="mt-6 space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
              <ToastTester />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <Mail className="w-6 h-6 mr-3 text-primary-600" />
                Email Notifications Tester
              </h2>
              <EmailTester user={user} />
            </div>
          </div>
        );
        
      case 'windows':
        return (
          <div className="mt-6 space-y-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Window Activation Management</h3>
              <WindowActivationButtonDemo />
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">All Survey Windows</h3>
              <AllSurveyWindows />
            </div>
          </div>
        );
        
      case 'irt-scoring':
        return (
          <div className="mt-6">
            <AdaptestTester />
          </div>
        );

      case 'participant-reset':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <ParticipantResetTool />
          </div>
        );

      case 'seed-test-participant':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <SeedTestParticipantTool />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {testingTools.map((tool) => (
          <ToolCard
            key={tool.id}
            tool={tool}
            onClick={handleToolClick}
            className={selectedTool === tool.id ? 'ring-2 ring-primary-500' : ''}
          />
        ))}
      </div>

      {/* Render selected tool content */}
      {selectedTool && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          {renderToolContent(selectedTool)}
        </motion.div>
      )}
    </div>
  );
};

export default TestingTools;
